import { ComponentPortal } from '@angular/cdk/portal';
import * as i0 from '@angular/core';
import { inject, InjectionToken, Injectable, Inject, EventEmitter, Component, Output, ViewChild, Input, NgModule } from '@angular/core';
import { throwError, ReplaySubject, tap } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { DOCUMENT, NgIf } from '@angular/common';
import { LinkComponent, IconComponent, ConfirmationDialogComponent, ButtonComponent, ContentDialogComponent, OneTimePasswordComponent } from '@pure/hive';
import * as i2 from '@pure/pure1-ui-platform-angular';
import { Pure1AppLinkPipe } from '@pure/pure1-ui-platform-angular';
import * as i1 from '@angular/common/http';
import { HttpParams } from '@angular/common/http';
import * as i4 from '@angular/forms';
import { FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import * as i2$1 from '@angular/cdk/overlay';
import * as i3 from '@pure/authz-authorizer';

const windowFactory = () => {
    const { defaultView } = inject(DOCUMENT);
    if (!defaultView) {
        throw new Error('Window is not available');
    }
    return defaultView;
};
const WINDOW = new InjectionToken('An abstraction over global window object', {
    providedIn: 'root',
    factory: windowFactory,
});

const AMPLITUDE_TRACK_CALLBACK = new InjectionToken('AMPLITUDE_TRACK_CALLBACK');

class GenericAmplitudeEvent {
    constructor(event_type, event_properties) {
        this.event_type = event_type;
        this.event_properties = event_properties;
    }
}
/**
 * Service for tracking events with Amplitude. Needs to have amplitude enabled in the platform configuration
 * and the Amplitude itself must be initialised in order for tracking to work.
 */
class AmplitudeService {
    constructor(amplitudeTrackCallback) {
        this.amplitudeTrackCallback = amplitudeTrackCallback;
    }
    /**
     * Tries to track amplitude event. Does nothing if Amplitude callback has not been set at module import.
     * @param event Amplitude event to track
     * @param options Event options
     * @returns
     */
    tryTrackEvent(event, options) {
        this.amplitudeTrackCallback(event, options);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AmplitudeService, deps: [{ token: AMPLITUDE_TRACK_CALLBACK }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AmplitudeService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AmplitudeService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [AMPLITUDE_TRACK_CALLBACK]
                }] }] });

class EnrollmentRequiredModalComponent {
    constructor(amplitudeService, navigationService, window) {
        this.amplitudeService = amplitudeService;
        this.navigationService = navigationService;
        this.window = window;
        this.dialogClosed = new EventEmitter();
        this.STEP_UP_SUPPORT_LINK = 'https://support.purestorage.com/bundle/m_getting_support/page/Pure1/Pure1_Administration/Pure1_Troubleshooting/library/common_content/o_identity_management_with_stepup_authentication.html';
    }
    close() {
        this.amplitudeService.tryTrackEvent({
            event_type: 'Step Up - Enrollment Required Modal - close',
            event_properties: { category: 'Action' },
        });
        this.enrollmentRequiredModal.hide();
    }
    trackLearnMore() {
        this.amplitudeService.tryTrackEvent({
            event_type: 'Step Up - Enrollment Required Modal - learn more',
            event_properties: { category: 'Action' },
        });
    }
    ngAfterViewInit() {
        this.enrollmentRequiredModal.show();
    }
    openIdentityCenter() {
        this.navigationService.link('identity-center', '/profile/account-security?mfaSetupOpen=true').subscribe(url => {
            this.window.open(url, '_blank');
        });
        this.amplitudeService.tryTrackEvent({
            event_type: 'Pure Identity Center - Dialog - Launch',
            event_properties: { category: 'Action' },
        });
        this.enrollmentRequiredModal.hide();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: EnrollmentRequiredModalComponent, deps: [{ token: AmplitudeService }, { token: i2.Pure1NavigationService }, { token: WINDOW }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: EnrollmentRequiredModalComponent, isStandalone: true, selector: "step-up-enrollment-required-modal", outputs: { dialogClosed: "dialogClosed" }, viewQueries: [{ propertyName: "enrollmentRequiredModal", first: true, predicate: ["enrollmentRequiredModal"], descendants: true }], ngImport: i0, template: "<hive-confirmation-dialog\n    #enrollmentRequiredModal\n    emphasis=\"info\"\n    secondaryActionLabel=\"Cancel\"\n    primaryActionLabel=\"Set Up Multi-Factor Authentication\"\n    (cancel)=\"close()\"\n    (confirm)=\"openIdentityCenter()\">\n    <div class=\"hive-confirmation-dialog__body-icon hive-confirmation-dialog__body-icon--info\">\n        <hive-icon-component size=\"x-large\" icon=\"light/circle-info.svg\"></hive-icon-component>\n    </div>\n    <div class=\"hive-confirmation-dialog__text-content\">\n        <h6 class=\"hive-header-semibold\">Multi-Factor Authentication Required</h6>\n        <p class=\"hive-paragraph-small hive-paragraph-regular\">\n            We've added an extra level of security against unauthorized logins and access to sensitive functionality.\n            Once you set up step-up multifactor authentication, Pure1 will ask for a temporary one time passcode when\n            authentication is required.\n            <a hive-link external=\"true\" [href]=\"STEP_UP_SUPPORT_LINK\" target=\"_blank\" (click)=\"trackLearnMore()\">\n                Learn More\n            </a>\n        </p>\n    </div>\n</hive-confirmation-dialog>\n", dependencies: [{ kind: "component", type: LinkComponent, selector: "a[hive-link]", inputs: ["disabled", "size", "external"] }, { kind: "component", type: IconComponent, selector: "hive-icon-component", inputs: ["icon", "size"] }, { kind: "component", type: ConfirmationDialogComponent, selector: "hive-confirmation-dialog", inputs: ["autofocus", "emphasis", "heading", "description", "icon", "primaryActionLabel", "secondaryActionLabel", "secondaryActionHidden", "isLoading"], outputs: ["confirm", "cancel"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: EnrollmentRequiredModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'step-up-enrollment-required-modal', standalone: true, imports: [
                        LinkComponent,
                        ButtonComponent,
                        Pure1AppLinkPipe,
                        IconComponent,
                        ContentDialogComponent,
                        NgIf,
                        ConfirmationDialogComponent,
                    ], template: "<hive-confirmation-dialog\n    #enrollmentRequiredModal\n    emphasis=\"info\"\n    secondaryActionLabel=\"Cancel\"\n    primaryActionLabel=\"Set Up Multi-Factor Authentication\"\n    (cancel)=\"close()\"\n    (confirm)=\"openIdentityCenter()\">\n    <div class=\"hive-confirmation-dialog__body-icon hive-confirmation-dialog__body-icon--info\">\n        <hive-icon-component size=\"x-large\" icon=\"light/circle-info.svg\"></hive-icon-component>\n    </div>\n    <div class=\"hive-confirmation-dialog__text-content\">\n        <h6 class=\"hive-header-semibold\">Multi-Factor Authentication Required</h6>\n        <p class=\"hive-paragraph-small hive-paragraph-regular\">\n            We've added an extra level of security against unauthorized logins and access to sensitive functionality.\n            Once you set up step-up multifactor authentication, Pure1 will ask for a temporary one time passcode when\n            authentication is required.\n            <a hive-link external=\"true\" [href]=\"STEP_UP_SUPPORT_LINK\" target=\"_blank\" (click)=\"trackLearnMore()\">\n                Learn More\n            </a>\n        </p>\n    </div>\n</hive-confirmation-dialog>\n" }]
        }], ctorParameters: () => [{ type: AmplitudeService }, { type: i2.Pure1NavigationService }, { type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }], propDecorators: { dialogClosed: [{
                type: Output
            }], enrollmentRequiredModal: [{
                type: ViewChild,
                args: ['enrollmentRequiredModal']
            }] } });

var ProductLine;
(function (ProductLine) {
    ProductLine["FlashArray"] = "FlashArray";
    ProductLine["FlashBlade"] = "FlashBlade";
    ProductLine["Portworx"] = "Portworx";
    ProductLine["Others"] = "Other";
    ProductLine["FlashRecover"] = "FlashRecover";
    ProductLine["DisasterRecovery"] = "PureProtect";
    ProductLine["None"] = "";
})(ProductLine || (ProductLine = {}));
var CaseSeverity;
(function (CaseSeverity) {
    CaseSeverity["CRITICAL"] = "1";
    CaseSeverity["MAJOR"] = "2";
    CaseSeverity["MINOR"] = "3";
    CaseSeverity["INFORMATIONAL"] = "4";
})(CaseSeverity || (CaseSeverity = {}));
var CaseCategory;
(function (CaseCategory) {
    // Normal cases
    CaseCategory["ADMINISTRATIVE"] = "3";
    CaseCategory["EVERGREENONEADMINISTRATION"] = "30";
    CaseCategory["ENVIRONMENTALS"] = "4";
    CaseCategory["HARDWARE"] = "5";
    CaseCategory["SOFTWARE"] = "6";
})(CaseCategory || (CaseCategory = {}));
// Subcategories are prefixed with main category for clarity which subcategory belongs to which category
var CaseSubCategory;
(function (CaseSubCategory) {
    // ADMINISTRATIVE
    CaseSubCategory["ADMINISTRATIVE_GENERAL_INQUIRY"] = "172";
    // SOFTWARE
    CaseSubCategory["SOFTWARE_SOFTWARE"] = "197";
})(CaseSubCategory || (CaseSubCategory = {}));

class SupportCenterRedirectService {
    constructor(navigationService, window) {
        this.navigationService = navigationService;
        this.window = window;
    }
    openSupportCenter(caseId) {
        if (caseId) {
            this.navigationService.link('support-center', `cases/${caseId}`).subscribe(url => {
                this.window.open(url, '_blank');
            });
        }
        else {
            this.navigationService.link('support-center').subscribe(url => {
                this.window.open(url, '_blank');
            });
        }
    }
    openCreateCase(supportCase, searchArray) {
        let params = new HttpParams();
        if (supportCase) {
            if (supportCase.category) {
                params = params.append('category', supportCase.category);
            }
            if (supportCase.subCategory) {
                params = params.append('subCategory', supportCase.subCategory);
            }
            if (supportCase.severity) {
                params = params.append('severity', supportCase.severity);
            }
            if (supportCase.subject) {
                params = params.append('subject', supportCase.subject);
            }
            if (supportCase.description) {
                params = params.append('description', supportCase.description);
            }
            if (supportCase.productLine) {
                params = params.append('productLine', supportCase.productLine);
            }
        }
        if (searchArray) {
            params = params.append('searchArray', searchArray);
        }
        this.navigationService.link('support-center', `createCase?${params.toString()}`).subscribe(url => {
            this.window.open(url, '_blank');
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SupportCenterRedirectService, deps: [{ token: i2.Pure1NavigationService }, { token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SupportCenterRedirectService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SupportCenterRedirectService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i2.Pure1NavigationService }, { type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }] });

const REQUEST_STEP_UP_UNLOCK = {
    productLine: ProductLine.Others,
    subject: 'Request to Unlock Account',
    description: 'Locked out during Step-Up authentication. Please unlock account.',
    category: CaseCategory.ADMINISTRATIVE,
    subCategory: CaseSubCategory.ADMINISTRATIVE_GENERAL_INQUIRY,
    severity: CaseSeverity.INFORMATIONAL,
};
const NEED_HELP_WITH_CHALLENGE = {
    productLine: ProductLine.Others,
    subject: 'Need Help With Two Factor Authentication',
    description: 'Cannot verify passcode for two factor authentication.',
    category: CaseCategory.ADMINISTRATIVE,
    subCategory: CaseSubCategory.ADMINISTRATIVE_GENERAL_INQUIRY,
    severity: CaseSeverity.INFORMATIONAL,
};
class StepUpSupportService {
    constructor(supportCenterRedirectService) {
        this.supportCenterRedirectService = supportCenterRedirectService;
    }
    openStepUpUnlockRequestCase() {
        this.supportCenterRedirectService.openCreateCase(REQUEST_STEP_UP_UNLOCK);
    }
    openStepUpChallengeHelpCase() {
        this.supportCenterRedirectService.openCreateCase(NEED_HELP_WITH_CHALLENGE);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StepUpSupportService, deps: [{ token: SupportCenterRedirectService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StepUpSupportService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StepUpSupportService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: SupportCenterRedirectService }] });

class StepUpErrorMessageModalComponent {
    constructor(stepUpSupport, amplitudeService) {
        this.stepUpSupport = stepUpSupport;
        this.amplitudeService = amplitudeService;
        this.showSupportButton = false;
        this.dialogClosed = new EventEmitter();
    }
    close() {
        this.amplitudeService.tryTrackEvent({
            event_type: 'Step Up - Error Modal - close modal',
            event_properties: { category: 'Action' },
        });
        this.stepUpErrorMessageModal.hide();
    }
    goToSupport() {
        this.amplitudeService.tryTrackEvent({
            event_type: 'Step Up - Error Modal - go to support',
            event_properties: { category: 'Action' },
        });
        this.stepUpSupport.openStepUpUnlockRequestCase();
        this.close();
    }
    ngAfterViewInit() {
        this.stepUpErrorMessageModal.show();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StepUpErrorMessageModalComponent, deps: [{ token: StepUpSupportService }, { token: AmplitudeService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: StepUpErrorMessageModalComponent, isStandalone: true, selector: "step-up-error-message-modal", inputs: { errorBody: "errorBody", showSupportButton: "showSupportButton", error: "error" }, outputs: { dialogClosed: "dialogClosed" }, viewQueries: [{ propertyName: "stepUpErrorMessageModal", first: true, predicate: ["stepUpErrorMessageModal"], descendants: true }], ngImport: i0, template: "<hive-confirmation-dialog\n    #stepUpErrorMessageModal\n    [heading]=\"errorBody.title\"\n    emphasis=\"destructive\"\n    [description]=\"errorBody.message\"\n    [secondaryActionLabel]=\"errorBody.closeLabel\"\n    [primaryActionLabel]=\"\n    showSupportButton ? 'Contact Pure Technical Services' : errorBody.closeLabel\n  \"\n    (cancel)=\"close()\"\n    (confirm)=\"showSupportButton ? goToSupport() : close()\"\n    [secondaryActionHidden]=\"!showSupportButton\"></hive-confirmation-dialog>\n", dependencies: [{ kind: "component", type: ConfirmationDialogComponent, selector: "hive-confirmation-dialog", inputs: ["autofocus", "emphasis", "heading", "description", "icon", "primaryActionLabel", "secondaryActionLabel", "secondaryActionHidden", "isLoading"], outputs: ["confirm", "cancel"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StepUpErrorMessageModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'step-up-error-message-modal', standalone: true, imports: [
                        IconComponent,
                        ButtonComponent,
                        NgIf,
                        LinkComponent,
                        OneTimePasswordComponent,
                        ConfirmationDialogComponent,
                    ], template: "<hive-confirmation-dialog\n    #stepUpErrorMessageModal\n    [heading]=\"errorBody.title\"\n    emphasis=\"destructive\"\n    [description]=\"errorBody.message\"\n    [secondaryActionLabel]=\"errorBody.closeLabel\"\n    [primaryActionLabel]=\"\n    showSupportButton ? 'Contact Pure Technical Services' : errorBody.closeLabel\n  \"\n    (cancel)=\"close()\"\n    (confirm)=\"showSupportButton ? goToSupport() : close()\"\n    [secondaryActionHidden]=\"!showSupportButton\"></hive-confirmation-dialog>\n" }]
        }], ctorParameters: () => [{ type: StepUpSupportService }, { type: AmplitudeService }], propDecorators: { errorBody: [{
                type: Input
            }], showSupportButton: [{
                type: Input
            }], error: [{
                type: Input
            }], dialogClosed: [{
                type: Output
            }], stepUpErrorMessageModal: [{
                type: ViewChild,
                args: ['stepUpErrorMessageModal']
            }] } });

class StepUpFactor {
    constructor(json) {
        this.id = json.id;
        this.factorType = json.factor_type;
        this.status = json.status;
        this.profile = {
            phoneNumber: json.profile?.phone_number,
            email: json.profile?.email,
            secret: json.profile?.secret,
            barcodeUri: json.profile?.barcode_uri,
        };
    }
}
class StepUpChallengeResponse {
    constructor(json) {
        this.stateToken = json.state_token;
        this.factorId = json.factor_id;
    }
}
class StepUpVerifyResponse {
    constructor(json) {
        this.accessToken = json.access_token;
        this.expiresIn = json.expires_in;
        this.scope = json.scope;
        this.authorizationDetails = json.authorization_details;
    }
}

const STEP_UP_ERROR_RESPONSES = {
    invalid_request: {
        error: 'invalid_request',
        errorDescription: 'Invalid request.',
    },
    enrollment_required: {
        error: 'enrollment_required',
        errorDescription: 'Prior enrollment for Step-up Authentication required.',
    },
    bad_passcode: {
        error: 'bad_passcode',
        errorDescription: 'Passcode incorrect.',
    },
    challenge_expired: {
        error: 'challenge_expired',
        errorDescription: 'Authentication challenge expired.',
    },
    locked_out: {
        error: 'locked_out',
        errorDescription: 'Too many failed authentication requests.',
    },
    impersonate_not_allowed: {
        error: 'impersonate_not_allowed',
        errorDescription: 'Step Up is not permitted while switched to another user.',
    },
};
const STEP_UP_ENDPOINT_V2 = '/rest/v2/oob';
class StepUpService {
    constructor(http) {
        this.http = http;
    }
    getFactorsV2() {
        return this.http.get(`${STEP_UP_ENDPOINT_V2}/factors`).pipe(catchError((error) => this.transformToStepUpError(error)), map((response) => (response ? response.map(json => new StepUpFactor(json)) : [])));
    }
    stepUpChallenge(factorId, audience, details, singleUse, ttl) {
        return this.http
            .post(`${STEP_UP_ENDPOINT_V2}/challenge`, {
            factor_id: factorId,
            audience,
            single_use: singleUse,
            authorization_details: details,
            ttl,
        })
            .pipe(catchError((error) => this.transformToStepUpError(error)), map((response) => new StepUpChallengeResponse(response)));
    }
    stepUpVerify(passCode, stateToken, factorId) {
        return this.http
            .post(`${STEP_UP_ENDPOINT_V2}/verify`, {
            pass_code: passCode,
            state_token: stateToken,
            factor_id: factorId,
        })
            .pipe(catchError((error) => this.transformToStepUpError(error)), map((response) => new StepUpVerifyResponse(response)));
    }
    transformToStepUpError(error) {
        if (error.error) {
            return throwError({
                error: error.error.error,
                errorDescription: error.error.error_description,
            });
        }
        else {
            return throwError({
                error: 'unknown_error',
                errorDescription: 'Unknown error occurred',
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StepUpService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StepUpService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StepUpService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.HttpClient }] });

const DEFAULT_TTL_SECONDS = 300;
class TotpStepUpChallengeComponent {
    constructor(stepUpService, stepUpSupport, amplitudeService) {
        this.stepUpService = stepUpService;
        this.stepUpSupport = stepUpSupport;
        this.amplitudeService = amplitudeService;
        this.dialogClosed = new EventEmitter();
        this.challengeResponse = null;
        this.loadingVeryfing = false;
        // Form control handling
        this.verifyCodeInputControl = new FormControl('', [
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(6),
        ]);
    }
    ngOnInit() {
        if (this.factors && this.factors.length > 0) {
            this.factor = this.factors.find(factor => factor.factorType === 'totp' && factor.status === 'ACTIVE');
        }
        if (!this.factor) {
            console.warn('no step up factor found for the user');
        }
        this.verifyCodeInputControl.valueChanges.subscribe(value => {
            if (this.verifyCodeInputControl.valid) {
                this.passcodeInputValue = value;
            }
            else {
                this.passcodeInputValue = null;
            }
        });
        this.challenge();
    }
    challenge() {
        // issue the challenge
        this.amplitudeService.tryTrackEvent({
            event_type: 'Step Up - Challenge Modal - passcode challenge issued',
            event_properties: { category: 'Action', label: this.factor.factorType },
        });
        this.stepUpService
            .stepUpChallenge(this.factor.id, this.audience, this.authorizationDetails, this.singleUse, this.ttl != null ? this.ttl : DEFAULT_TTL_SECONDS)
            .subscribe({
            next: response => {
                this.challengeResponse = response;
            },
            error: (error) => {
                console.warn(`error issuing challenge: ${error.errorDescription}`);
                this.errorMessage = error.errorDescription;
            },
        });
    }
    verify() {
        this.loadingVeryfing = true;
        this.amplitudeService.tryTrackEvent({
            event_type: 'Step Up - Challenge Modal - submit passcode',
            event_properties: { category: 'Action' },
        });
        this.stepUpService
            .stepUpVerify(this.passcodeInputValue, this.challengeResponse.stateToken, this.factor.id)
            .subscribe({
            next: (response) => {
                this.amplitudeService.tryTrackEvent({
                    event_type: 'Step Up - Challenge Modal - passcode verified successfully',
                    event_properties: { category: 'Action' },
                });
                this.totpStepUpChallengeDialog.hide();
                this.dialogClosed.next(response);
            },
            error: (error) => this.handleStepUpError(error),
        });
    }
    goToSupport() {
        this.amplitudeService.tryTrackEvent({
            event_type: 'Step Up - Challenge Modal - contact support',
            event_properties: { category: 'Action' },
        });
        this.stepUpSupport.openStepUpChallengeHelpCase();
        this.totpStepUpChallengeDialog.hide();
    }
    close() {
        this.amplitudeService.tryTrackEvent({
            event_type: 'Step Up - Challenge Modal - cancel',
            event_properties: { category: 'Action' },
        });
        this.totpStepUpChallengeDialog.hide();
    }
    handleStepUpError(error) {
        this.errorMessage = error.errorDescription;
        this.loadingVeryfing = false;
        if (error.error === 'locked_out') {
            this.amplitudeService.tryTrackEvent({
                event_type: 'Step Up - Challenge Modal - passcode lockout',
                event_properties: { category: 'Action' },
            });
        }
        this.amplitudeService.tryTrackEvent({
            event_type: 'Step Up - Challenge Modal - challenge error',
            event_properties: { category: 'Action', label: error.error },
        });
    }
    ngAfterViewInit() {
        this.totpStepUpChallengeDialog.show();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: TotpStepUpChallengeComponent, deps: [{ token: StepUpService }, { token: StepUpSupportService }, { token: AmplitudeService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: TotpStepUpChallengeComponent, isStandalone: true, selector: "step-up-totp-step-up-challenge", inputs: { factors: "factors", audience: "audience", authorizationDetails: "authorizationDetails", singleUse: "singleUse", ttl: "ttl" }, outputs: { dialogClosed: "dialogClosed" }, viewQueries: [{ propertyName: "totpStepUpChallengeDialog", first: true, predicate: ["totpStepUpChallengeDialog"], descendants: true }], ngImport: i0, template: "<hive-content-dialog #totpStepUpChallengeDialog showCloseButton heading=\"Two Factor Authentication\" size=\"small\">\n    <hive-one-time-password\n        label=\"Enter the code generated by your authenticator app\"\n        [formControl]=\"verifyCodeInputControl\"></hive-one-time-password>\n    <div class=\"totp-error-message\" *ngIf=\"errorMessage\">{{ errorMessage }}</div>\n\n    <div class=\"text\">\n        Need help?\n        <a hive-link external=\"true\" (click)=\"goToSupport()\">Contact Pure Technical Services</a>\n    </div>\n\n    <hive-button\n        width=\"fit-parent\"\n        emphasis=\"primary\"\n        label=\"Verify\"\n        size=\"small\"\n        (click)=\"verify()\"\n        [isLoading]=\"loadingVeryfing\"\n        [disabled]=\"passcodeInputValue == null\"></hive-button>\n\n    <hive-button\n        class=\"close-button\"\n        width=\"fit-parent\"\n        emphasis=\"tertiary\"\n        label=\"Cancel\"\n        size=\"small\"\n        (click)=\"close()\"></hive-button>\n</hive-content-dialog>\n", styles: [".text{margin-bottom:16px;margin-top:16px;text-align:center}.totp-error-message{color:red;text-align:center;margin-top:16px}.close-button{margin-top:16px}\n"], dependencies: [{ kind: "directive", type: NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: OneTimePasswordComponent, selector: "hive-one-time-password", inputs: ["label", "errorMessages", "assistanceText", "value", "autofocus", "size", "disabled", "length"], outputs: ["blur", "input"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i4.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: ContentDialogComponent, selector: "hive-content-dialog", inputs: ["heading", "description", "headerInfoMessage", "size", "isLoading", "showCloseButton", "noPadding", "closeOnBackdropClick"], outputs: ["contentDialogClose"] }, { kind: "component", type: ButtonComponent, selector: "hive-button", inputs: ["type", "menuButtonActive", "hasMenuButtonCaret", "label", "accessibleName", "icon", "iconPosition", "emphasis", "title", "size", "isLoading", "disabled", "rounded", "width"] }, { kind: "component", type: LinkComponent, selector: "a[hive-link]", inputs: ["disabled", "size", "external"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: TotpStepUpChallengeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'step-up-totp-step-up-challenge', standalone: true, imports: [
                        NgIf,
                        OneTimePasswordComponent,
                        ReactiveFormsModule,
                        ContentDialogComponent,
                        ButtonComponent,
                        LinkComponent,
                    ], template: "<hive-content-dialog #totpStepUpChallengeDialog showCloseButton heading=\"Two Factor Authentication\" size=\"small\">\n    <hive-one-time-password\n        label=\"Enter the code generated by your authenticator app\"\n        [formControl]=\"verifyCodeInputControl\"></hive-one-time-password>\n    <div class=\"totp-error-message\" *ngIf=\"errorMessage\">{{ errorMessage }}</div>\n\n    <div class=\"text\">\n        Need help?\n        <a hive-link external=\"true\" (click)=\"goToSupport()\">Contact Pure Technical Services</a>\n    </div>\n\n    <hive-button\n        width=\"fit-parent\"\n        emphasis=\"primary\"\n        label=\"Verify\"\n        size=\"small\"\n        (click)=\"verify()\"\n        [isLoading]=\"loadingVeryfing\"\n        [disabled]=\"passcodeInputValue == null\"></hive-button>\n\n    <hive-button\n        class=\"close-button\"\n        width=\"fit-parent\"\n        emphasis=\"tertiary\"\n        label=\"Cancel\"\n        size=\"small\"\n        (click)=\"close()\"></hive-button>\n</hive-content-dialog>\n", styles: [".text{margin-bottom:16px;margin-top:16px;text-align:center}.totp-error-message{color:red;text-align:center;margin-top:16px}.close-button{margin-top:16px}\n"] }]
        }], ctorParameters: () => [{ type: StepUpService }, { type: StepUpSupportService }, { type: AmplitudeService }], propDecorators: { factors: [{
                type: Input
            }], audience: [{
                type: Input
            }], authorizationDetails: [{
                type: Input
            }], singleUse: [{
                type: Input
            }], ttl: [{
                type: Input
            }], dialogClosed: [{
                type: Output
            }], totpStepUpChallengeDialog: [{
                type: ViewChild,
                args: ['totpStepUpChallengeDialog']
            }] } });

/**
 * Like its base class StepUpService, but maintains a locally cached copy of factors.
 * - StepUpChallenge and stepUpVerify: use base class, because they don't manipulate factors.
 * - Other methods listed here:
 *   - Get results from corresponding methods in base class.
 *   - Update the local cache before emitting the latest state of the cache to factors$ for all subscribers of
 *     getFactors method.
 */
class CachedStepUpService extends StepUpService {
    constructor() {
        super(...arguments);
        // Stores and emits the latest copy of step-up factors during initial get or whenever change happens.
        this.factors$ = new ReplaySubject(1);
        this.sentFirstRequest = false;
    }
    getFactorsV2() {
        if (this.sentFirstRequest) {
            return this.factors$;
        }
        return this.getFactorsForceRefreshV2();
    }
    getFactorsForceRefreshV2() {
        this.sentFirstRequest = true;
        return super.getFactorsV2().pipe(catchError(error => {
            this.factors = null; // Previous results are out-of-date. Force refresh next time.
            // On error, there is no way to get further results in the future.
            // Subscribers will just have to call getFactors or getFactorsForceRefresh again.
            this.factors$.error(error);
            return this.factors$;
        }), switchMap(results => {
            this.factors = new Map();
            results.forEach(result => {
                this.factors.set(result.id, result);
            });
            this.factors$.next(Array.from(this.factors.values()));
            // Return factors$ so that subscribers not only get results from this call,
            // but also any updates in the future.
            return this.factors$;
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: CachedStepUpService, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: CachedStepUpService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: CachedStepUpService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const IMPERSONATE_MODAL_ERROR = {
    title: 'Access Denied',
    message: 'You cannot perform this action while switched to another user.',
    closeLabel: 'OK',
};
const LOCKED_OUT_MODAL_ERROR = {
    title: 'You Are Temporarily Locked Out',
    message: 'Your account has been temporarily locked. There have been too many invalid verification code attempts during login. Please try again in an hour or contact Pure Technical Services to unlock your account.',
    closeLabel: 'Okay, I will wait',
};
class StepUpModalService {
    constructor(cachedStepUpService, overlay, authorizationService) {
        this.cachedStepUpService = cachedStepUpService;
        this.overlay = overlay;
        this.authorizationService = authorizationService;
    }
    stepUp(audience, authorizationDetails, singleUse, ttl) {
        return this.authorizationService.isAllowed('PURE1:write:step_up').pipe(take(1), switchMap(canWriteStepup => {
            if (!canWriteStepup) {
                return this.showErrorModal(IMPERSONATE_MODAL_ERROR, STEP_UP_ERROR_RESPONSES.impersonate_not_allowed);
            }
            return this.cachedStepUpService.getFactorsV2().pipe(take(1), catchError((error) => {
                if (error.error === 'locked_out') {
                    return this.showErrorModal(LOCKED_OUT_MODAL_ERROR, STEP_UP_ERROR_RESPONSES.locked_out, true);
                }
                else {
                    console.warn('unknown factor error encountered: ' + error);
                    return throwError(() => error);
                }
            }), switchMap((result) => {
                if (result.some(factor => factor.factorType === 'totp' && factor.status === 'ACTIVE')) {
                    this.showDialog(TotpStepUpChallengeComponent);
                    Object.assign(this.attachedDialog.instance, {
                        factors: result,
                        audience,
                        authorizationDetails,
                        singleUse,
                        ttl,
                    });
                }
                else {
                    this.showDialog(EnrollmentRequiredModalComponent);
                }
                return this.attachedDialog.instance.dialogClosed.pipe(take(1), tap(() => {
                    this.overlayRef.dispose();
                }));
            }));
        }));
    }
    showErrorModal(errorBody, resultError, showSupportButton = false) {
        this.showDialog(StepUpErrorMessageModalComponent);
        Object.assign(this.attachedDialog.instance, {
            errorBody,
            resultError,
            showSupportButton,
        });
        return this.attachedDialog.instance.dialogClosed.pipe(take(1), tap(() => {
            this.overlayRef.dispose();
        }));
    }
    showDialog(component) {
        const overlayRef = this.overlay.create();
        const overlayPortal = new ComponentPortal(component);
        this.attachedDialog = overlayRef.attach(overlayPortal);
        this.overlayRef = overlayRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StepUpModalService, deps: [{ token: CachedStepUpService }, { token: i2$1.Overlay }, { token: i3.AuthorizationService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StepUpModalService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StepUpModalService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: CachedStepUpService }, { type: i2$1.Overlay }, { type: i3.AuthorizationService }] });

class StepUpModule {
    static forRoot(config) {
        const fallthroughAmplitudeTrackCallback = () => { };
        return {
            ngModule: StepUpModule,
            providers: [
                StepUpModalService,
                AmplitudeService,
                {
                    provide: AMPLITUDE_TRACK_CALLBACK,
                    useValue: config.amplitudeTrackCallback || fallthroughAmplitudeTrackCallback,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StepUpModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: StepUpModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StepUpModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: StepUpModule, decorators: [{
            type: NgModule
        }] });

/*
 * Public API Surface of step-up-angular
 */

/**
 * Generated bundle index. Do not edit.
 */

export { StepUpModalService, StepUpModule };
